'use client';

import { cn } from '@/lib/utils';
import { useT } from '@/components/i18n-provider';
import type { ResizeAnchor } from './use-node-resize';

/**
 * Invisible edge / corner hit area for desktop node resize.
 *
 * Each handle sits ON the edge of the card (offset by 1.5–2px outward
 * so the cursor picks up `*-resize` slightly before the user touches
 * the visible edge — feels precise without forcing pixel-perfect aim).
 *
 * The class `nodrag nopan` (in addition to `stopPropagation` in the
 * pointer-down handler) keeps the React Flow background pan from
 * stealing the gesture while the user is mid-resize. Corner handles
 * are placed on top of the edge handles via z-index so a click at a
 * corner doesn't land on the wrong edge.
 *
 * Used by both `NodeFrame` (widgets) and `AnnotationNode` (sticky /
 * text / shape annotations). The visual cursor is the entire UI for
 * "this is resizable" — no bounding box, no handles. Mirrors the
 * gesture model Figma uses for canvas frames.
 */
export function ResizeEdge({
  position,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: {
  position: ResizeAnchor;
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}) {
  const t = useT();
  const isCorner =
    position === 'tl' ||
    position === 'tr' ||
    position === 'bl' ||
    position === 'br';
  return (
    <div
      role="separator"
      aria-orientation={
        position === 't' || position === 'b' ? 'horizontal' : 'vertical'
      }
      aria-label={t('workspace.node.resize')}
      data-edge={position}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      className={cn(
        // `ws-resize-edge` is the marker class CSS uses to disable
        // these hit zones when an annotation / connector tool is
        // armed (see `.ws-canvas-tool .ws-resize-edge` in
        // `globals.css`) — keeps the connector grab affordance from
        // being stolen by an invisible resize strip.
        'ws-resize-edge nodrag nopan absolute pointer-events-auto',
        // Corners win the z stack so they're not eaten by adjacent edges.
        isCorner ? 'z-30' : 'z-20',
        // Edge hit zones are thin (3px) but stretch the full edge.
        position === 't' && 'left-0 right-0 -top-1.5 h-3 cursor-ns-resize',
        position === 'b' && 'left-0 right-0 -bottom-1.5 h-3 cursor-ns-resize',
        position === 'l' && 'top-0 bottom-0 -left-1.5 w-3 cursor-ew-resize',
        position === 'r' && 'top-0 bottom-0 -right-1.5 w-3 cursor-ew-resize',
        // Corners are 6x6 squares — generous touch target while staying
        // out of the way of the body click area.
        position === 'tl' && '-top-2 -left-2 h-6 w-6 cursor-nwse-resize',
        position === 'tr' && '-top-2 -right-2 h-6 w-6 cursor-nesw-resize',
        position === 'bl' && '-bottom-2 -left-2 h-6 w-6 cursor-nesw-resize',
        position === 'br' && '-bottom-2 -right-2 h-6 w-6 cursor-nwse-resize',
      )}
    />
  );
}
